import React, { useState } from "react";
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    ScrollView,
    Linking,
    Platform
} from "react-native";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from "@react-navigation/native";
import TopGreenHeader from "../Common/TopGreenHeader";
import { loginActions } from "../../slices/authSlice";

// The schedule screen stores the location service type (LEARNCARE / TRANSLEARN /
// TRANSPORT) while the plans API keys on its own service codes. Map between them.
const PLAN_SERVICE_BY_TYPE = {
    LEARNCARE: "SOCIALHUB",
    TRANSLEARN: "BREAKFASTCLUB",
    TRANSPORT: "TRANSPORT",
};

// Breakfast Club runs a fixed Monday-Friday week.
const FIXED_WEEKDAY_SERVICES = ["BREAKFASTCLUB"];

const SeeAllPlans = () => {
    const navigation = useNavigation();
    const dispatch = useDispatch();
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const getAllPlans = useSelector(state => state.auth.getAllPlans);
    const serviceScheduled = useSelector(state => state.auth.serviceScheduled);
    const serviceName = serviceScheduled?.serviceName;

    // Fall back to serviceName so this keeps working if the API ever sends the
    // plan code directly.
    const planServiceCode = PLAN_SERVICE_BY_TYPE[serviceName] || serviceName;
    const isFixedWeekdays = FIXED_WEEKDAY_SERVICES.includes(planServiceCode);

    const plansForService = (getAllPlans?.Data || []).filter(
        plan => plan.service === planServiceCode
    );

    // Group plans: package → noOfKid → [plans]
    // Each "family card" groups plans with the same kid count under the same package.
    const groupedPackages = plansForService.reduce((acc, plan) => {
        let pkgGroup = acc.find(g => g.package === plan.package);
        if (!pkgGroup) {
            pkgGroup = { package: plan.package, families: [] };
            acc.push(pkgGroup);
        }
        let family = pkgGroup.families.find(f => f.noOfKid === plan.noOfKid);
        if (!family) {
            // Derive the family label from the plan name (text before ":" if present).
            const familyLabel = plan.name.includes(':')
                ? plan.name.split(':')[0].trim()
                : plan.name;
            family = { noOfKid: plan.noOfKid, label: familyLabel, plans: [] };
            pkgGroup.families.push(family);
        }
        family.plans.push(plan);
        return acc;
    }, []);

    const handleSeeAllPlan = () => {
        Linking.openURL("https://danilees.s3.us-east-2.amazonaws.com/DanileesPlan.pdf")
            .catch(err => console.error("Failed to open URL:", err));
    };

    const handleSelectPlan = (plan) => {
        setSelectedPlanId(plan.planId);
        dispatch(loginActions.setplanChoosed({
            choseedPlan: `${plan.type}|${plan.price}`,
            choseedPlanName: plan.name,
            isMonthlyPlanChoosed: true,
            planId: plan.planId,
            planType: plan.type,
            numOfKids: plan.noOfKid,
            minDays: plan.minDays,
            maxDays: plan.maxDays,
            price: plan.price,
            planService: planServiceCode,
            isFixedWeekdays,
        }));
        navigation.navigate("SelectDaysScreen");
    };

    return (
        <>
            <TopGreenHeader Heading={'Select Plan'} navigation={navigation} isBackIcon={true} isNotification={false} />
            <ScrollView style={styles.container}>
                <TouchableOpacity onPress={handleSeeAllPlan} style={styles.SeeALL}>
                    <Text style={styles.SeeALLText}>See All Plans</Text>
                </TouchableOpacity>

                {groupedPackages.length === 0 && (
                    <Text style={styles.emptyText}>No plans available for this service.</Text>
                )}

                {groupedPackages.map(group => (
                    <View key={group.package}>
                        <Text style={styles.header}>{group.package}</Text>
                        {group.families.map(family => (
                            <View key={`${group.package}-${family.noOfKid}`} style={styles.card}>
                                <View style={styles.cardHeaderRow}>
                                    <Text style={styles.planTitle}>
                                        {family.label}
                                    </Text>
                                    <Text style={styles.planTitle2}>
                                        {' '}({family.noOfKid > 1 ? `${family.noOfKid} children` : `${family.noOfKid} child`})
                                    </Text>
                                </View>
                                {family.plans.map(plan => (
                                    <TouchableOpacity
                                        key={plan.planId}
                                        style={styles.option}
                                        onPress={() => handleSelectPlan(plan)}>
                                        <Text style={styles.optionText}>
                                            {plan.type} | ${plan.price}
                                        </Text>
                                        <View style={styles.radioCircle}>
                                            {selectedPlanId === plan.planId && (
                                                <View style={styles.selectedCircle} />
                                            )}
                                        </View>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        ))}
                    </View>
                ))}
            </ScrollView>
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: "#f5f5f5",
        padding: 20,
    },
    header: {
        fontSize: 24,
        color: "#EE3897",
        marginVertical: 20,
        fontFamily: 'Facebook Sans'
    },
    emptyText: {
        fontSize: 16,
        color: "#666",
        textAlign: "center",
        marginTop: 40,
        fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
    },
    card: {
        backgroundColor: "#fff",
        borderRadius: 10,
        padding: 20,
        marginBottom: 15,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        elevation: 5,
    },
    cardHeaderRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
    },
    planTitle: {
        fontSize: 18,
        marginBottom: 10,
        color: "#505766",
        fontFamily: Platform.OS === "android" ? "FacebookSansBold" : "Facebook Sans",
        fontStyle: "normal",
    },
    planTitle2: {
        fontSize: 18,
        marginLeft: 5,
        marginBottom: 10,
        fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
        color: "#505766"
    },
    option: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 10,
        justifyContent: "space-between"
    },
    radioCircle: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: "#FF4081",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 10,
    },
    selectedCircle: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: "#FF4081",
    },
    optionText: {
        fontSize: 16,
        fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
        color: "#505766"
    },
    SeeALL: {
        backgroundColor: '#8dc63f',
        padding: 15,
        borderRadius: 10,
        alignItems: "center",
        fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
        fontStyle: "normal",
    },
    SeeALLText: {
        color: "#fff",
        fontSize: 18,
        fontWeight: "bold",
        fontFamily: Platform.OS === "android" ? "FacebookSansRegular" : "Facebook Sans",
        fontStyle: "normal",
    },
});

export default SeeAllPlans;
